/// Compass error — converting between the three norths a navigator
/// works with:
///
///   COMPASS  ──apply DEVIATION──▶  MAGNETIC  ──apply VARIATION──▶  TRUE
///   (steering compass)             (magnetic north)               (chart north)
///
/// - Deviation is the vessel's own magnetism. It changes with the
///   boat's heading and is read from a deviation card.
/// - Variation is the difference between magnetic and true north at a
///   place and date, read from the chart's compass rose.
///
/// Sign rule, going UP the chain (Compass → True): ADD easterly errors,
/// SUBTRACT westerly ones — the mnemonic **CADET** ("Compass ADd East
/// → True"). Going the other way (True → Compass) the rule reverses.
///
/// Companion check: "Error East, Compass Least; Error West, Compass
/// Best" — an easterly error makes the lower frame read less than the
/// upper, a westerly error makes it read more.
///
/// Pure logic, no DOM — the calculator and games in apps/web consume
/// this; it must stay testable under Vitest.

export type Hand = "E" | "W";

export type ReferenceFrame = "compass" | "magnetic" | "true";

export interface CompassError {
  /// Magnitude in degrees (>= 0). Whole degrees in the games; the
  /// maths works for fractional values too.
  degrees: number;
  /// Side of the reference north the error lies on.
  hand: Hand;
}

/// Normalise to the half-open range [0, 360).
export function normalizeDeg(deg: number): number {
  return ((deg % 360) + 360) % 360;
}

/// Three-digit heading string, e.g. 7 → "007", 90 → "090".
export function formatHeading(deg: number): string {
  return String(Math.round(normalizeDeg(deg))).padStart(3, "0");
}

/// "4°E", "6°W". Magnitude 0 is shown as "0°" (no hand — nil error).
export function formatError(error: CompassError): string {
  if (error.degrees === 0) return "0°";
  return `${error.degrees}°${error.hand}`;
}

/// Signed value used when correcting UPWARD (Compass → True): East is
/// positive, West negative. The downward direction negates this.
export function signedError(error: CompassError): number {
  return error.hand === "E" ? error.degrees : -error.degrees;
}

export interface ConversionStep {
  from: ReferenceFrame;
  fromValue: number;
  to: ReferenceFrame;
  toValue: number;
  /// Which error was applied in this step.
  errorName: "deviation" | "variation";
  error: CompassError;
  /// How the magnitude was combined to reach `toValue` — "+" when the
  /// degrees were added, "−" when subtracted.
  operation: "+" | "−";
}

export interface CompassSolution {
  compassDeg: number;
  magneticDeg: number;
  trueDeg: number;
  /// The working, in the reading order of the requested direction.
  steps: ConversionStep[];
}

function upStep(
  from: ReferenceFrame,
  fromValue: number,
  to: ReferenceFrame,
  errorName: "deviation" | "variation",
  error: CompassError,
): ConversionStep {
  // Going up the chain we add easterly, subtract westerly.
  const toValue = normalizeDeg(fromValue + signedError(error));
  return {
    from,
    fromValue,
    to,
    toValue,
    errorName,
    error,
    operation: error.hand === "E" ? "+" : "−",
  };
}

function downStep(
  from: ReferenceFrame,
  fromValue: number,
  to: ReferenceFrame,
  errorName: "deviation" | "variation",
  error: CompassError,
): ConversionStep {
  // Going down the chain the rule reverses: subtract easterly, add
  // westerly.
  const toValue = normalizeDeg(fromValue - signedError(error));
  return {
    from,
    fromValue,
    to,
    toValue,
    errorName,
    error,
    operation: error.hand === "E" ? "−" : "+",
  };
}

/// Correct a compass heading up to true. Steps: Compass →(deviation)→
/// Magnetic →(variation)→ True.
export function compassToTrue(
  compassDeg: number,
  variation: CompassError,
  deviation: CompassError,
): CompassSolution {
  const compass = normalizeDeg(compassDeg);
  const s1 = upStep("compass", compass, "magnetic", "deviation", deviation);
  const s2 = upStep("magnetic", s1.toValue, "true", "variation", variation);
  return {
    compassDeg: compass,
    magneticDeg: s1.toValue,
    trueDeg: s2.toValue,
    steps: [s1, s2],
  };
}

/// Uncorrect a true heading down to compass. Steps: True →(variation)→
/// Magnetic →(deviation)→ Compass.
export function trueToCompass(
  trueDeg: number,
  variation: CompassError,
  deviation: CompassError,
): CompassSolution {
  const trueValue = normalizeDeg(trueDeg);
  const s1 = downStep("true", trueValue, "magnetic", "variation", variation);
  const s2 = downStep("magnetic", s1.toValue, "compass", "deviation", deviation);
  return {
    compassDeg: s2.toValue,
    magneticDeg: s1.toValue,
    trueDeg: trueValue,
    steps: [s1, s2],
  };
}

/// Solve for all three frames given any one of them, returning the
/// working in the natural direction (up to true when starting from
/// compass/magnetic, down to compass when starting from true).
export function solve(
  frame: ReferenceFrame,
  value: number,
  variation: CompassError,
  deviation: CompassError,
): CompassSolution {
  switch (frame) {
    case "compass":
      return compassToTrue(value, variation, deviation);
    case "true":
      return trueToCompass(value, variation, deviation);
    case "magnetic": {
      // Build both halves out from magnetic: down to compass, up to true.
      const v = normalizeDeg(value);
      const toCompass = downStep("magnetic", v, "compass", "deviation", deviation);
      const toTrue = upStep("magnetic", v, "true", "variation", variation);
      return {
        compassDeg: toCompass.toValue,
        magneticDeg: v,
        trueDeg: toTrue.toValue,
        steps: [toCompass, toTrue],
      };
    }
  }
}

// ─── Mnemonic helpers (power the "East/West" sign-drill game) ──────

/// Going UP the chain (toward true), is an error of this hand added or
/// subtracted? East → add, West → subtract (CADET).
export function addOrSubtractUp(hand: Hand): "add" | "subtract" {
  return hand === "E" ? "add" : "subtract";
}

/// With an error of this hand, how does the LOWER frame read relative
/// to the UPPER? (e.g. compass vs magnetic, or magnetic vs true.)
/// East → less, West → greater.
export function lowerVsUpper(hand: Hand): "less" | "greater" {
  return hand === "E" ? "less" : "greater";
}

/// "Error East, Compass Least; Error West, Compass Best." Given the
/// hand of the (combined) error, does the compass read least or best?
export function compassLeastOrBest(hand: Hand): "least" | "best" {
  return hand === "E" ? "least" : "best";
}

// ─── Deviation card ───────────────────────────────────────────────

export interface DeviationRow {
  /// Compass heading the row applies to (every 30°).
  headingDeg: number;
  deviation: CompassError;
}

/// A representative deviation card — deviation swings easterly on
/// northerly/easterly headings and westerly on southerly/westerly
/// ones, peaking a few degrees either side, as a real small-craft
/// card typically does. Used by the Reference table and the
/// deviation-lookup drill.
export const SAMPLE_DEVIATION_CARD: readonly DeviationRow[] = Object.freeze([
  { headingDeg: 0, deviation: { degrees: 2, hand: "E" } },
  { headingDeg: 30, deviation: { degrees: 4, hand: "E" } },
  { headingDeg: 60, deviation: { degrees: 5, hand: "E" } },
  { headingDeg: 90, deviation: { degrees: 4, hand: "E" } },
  { headingDeg: 120, deviation: { degrees: 2, hand: "E" } },
  { headingDeg: 150, deviation: { degrees: 0, hand: "E" } },
  { headingDeg: 180, deviation: { degrees: 2, hand: "W" } },
  { headingDeg: 210, deviation: { degrees: 4, hand: "W" } },
  { headingDeg: 240, deviation: { degrees: 5, hand: "W" } },
  { headingDeg: 270, deviation: { degrees: 4, hand: "W" } },
  { headingDeg: 300, deviation: { degrees: 2, hand: "W" } },
  { headingDeg: 330, deviation: { degrees: 0, hand: "E" } },
]);

/// Smallest signed angular gap between two headings, in [-180, 180].
function angularGap(a: number, b: number): number {
  const diff = normalizeDeg(a - b);
  return diff > 180 ? diff - 360 : diff;
}

/// Read the deviation for a compass heading off a card by picking the
/// nearest tabulated row (cards are read, not interpolated, at this
/// level — the student looks up the closest heading).
export function deviationForHeading(
  card: readonly DeviationRow[],
  compassHeadingDeg: number,
): CompassError {
  const target = normalizeDeg(compassHeadingDeg);
  let best = card[0]!;
  let bestGap = Math.abs(angularGap(best.headingDeg, target));
  for (const row of card) {
    const gap = Math.abs(angularGap(row.headingDeg, target));
    if (gap < bestGap) {
      best = row;
      bestGap = gap;
    }
  }
  return best.deviation;
}
